import { MessageStates } from 'containers/MessageBox';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { UpgradeMessageSource } from 'store/api/enums';
import { turnMessageOn } from 'store/app/actions';
import { ScopeButton } from '@monorepo/shared-lib';
import DeleteEntityImage from '../../assets/images/delete-entity.svg';
import { ReactComponent as CheckIcon } from '../../assets/images/icons/checkIcon.svg';
import UpgradeAccountImage from '../../assets/images/upgrade-account.svg';
import ViesWarningIcon from '../../assets/images/vies-warning-icon.svg';

type Feature = string | { title: string; description: string };

interface UpgradeMessageContent {
  title: string;
  description: string;
  features: Feature[];
  buttonText: string;
  buttonType?: string;
  image?: string;
  secondButtonText?: string;
  secondButtonType?: string;
  hideFeatureIcon?: boolean;
  footerText?: string;
  footerTextBold?: boolean;
  showCancelButton?: boolean;
  cancelButtonText?: string;
  showBillingNote?: boolean;
  compactLayout?: boolean;
}

const getVatCheckerFeatures = (t: TFunction): Feature[] => [
  t('upgrade.vat-checker-feature-1'),
  t('upgrade.vat-checker-feature-2'),
  t('upgrade.vat-checker-feature-3'),
  t('upgrade.vat-checker-feature-4'),
];

const getContentConfig = (
  t: TFunction
): Record<UpgradeMessageSource, UpgradeMessageContent> => ({
  [UpgradeMessageSource.VAT_CHECKER]: {
    title: t('upgrade.vat-checker-title'),
    description: t('upgrade.recommend-upgrading-description'),
    features: getVatCheckerFeatures(t),
    buttonText: t('billing.upgrade-account'),
  },
  [UpgradeMessageSource.VAT_CHECKER_ACTIVATE]: {
    title: t('upgrade.vat-checker-activate-title'),
    description: '',
    features: getVatCheckerFeatures(t),
    buttonText: t('billing.activate'),
    buttonType: 'primary',
    image: ViesWarningIcon,
    showCancelButton: true,
    cancelButtonText: t('common.cancel'),
    showBillingNote: true,
    compactLayout: true,
  },
  [UpgradeMessageSource.EXCEPTIONS]: {
    title: t('upgrade.exceptions-title'),
    description: t('upgrade.upgrading-plan-access-description'),
    features: [
      t('upgrade.exceptions-feature-1'),
      t('upgrade.exceptions-feature-2'),
      t('upgrade.exceptions-feature-3'),
      t('upgrade.exceptions-feature-4'),
      t('upgrade.exceptions-feature-5'),
    ],
    buttonText: t('billing.upgrade-account'),
  },
  [UpgradeMessageSource.ATTACHMENT_VIEW]: {
    title: t('upgrade.attachment-view-title'),
    description: t('upgrade.upgrading-plan-access-description'),
    features: [
      t('upgrade.attachment-view-feature-1'),
      t('upgrade.attachment-view-feature-2'),
      t('upgrade.attachment-view-feature-3'),
      t('upgrade.attachment-view-feature-4'),
    ],
    buttonText: t('billing.upgrade-account'),
  },
  [UpgradeMessageSource.ATTACHMENT_UPLOAD]: {
    title: t('upgrade.attachment-upload-title'),
    description: t('upgrade.upgrading-plan-access-description'),
    features: [
      t('upgrade.attachment-upload-feature-1'),
      t('upgrade.attachment-view-feature-4'),
    ],
    buttonText: t('billing.upgrade-account'),
  },
  [UpgradeMessageSource.CLIENT_FLOW]: {
    title: t('upgrade.client-flow-title'),
    description: t('upgrade.recommend-upgrading-description'),
    features: [
      t('upgrade.client-flow-feature-1'),
      t('upgrade.client-flow-feature-2'),
      t('upgrade.client-flow-feature-3'),
      t('upgrade.client-flow-feature-4'),
      t('upgrade.client-flow-feature-5'),
    ],
    buttonText: t('billing.upgrade-account'),
  },
  [UpgradeMessageSource.ADVANCED_USER_RIGHTS]: {
    title: t('upgrade.advanced-user-rights-title'),
    description: t('upgrade.upgrading-plan-permissions-description'),
    features: [
      t('upgrade.advanced-user-rights-feature-1'),
      t('upgrade.advanced-user-rights-feature-2'),
      t('upgrade.advanced-user-rights-feature-3'),
      t('upgrade.advanced-user-rights-feature-4'),
      t('upgrade.advanced-user-rights-feature-5'),
    ],
    buttonText: t('billing.upgrade-account'),
  },
  [UpgradeMessageSource.MANAGER_VIEW]: {
    title: t('upgrade.manager-view-title'),
    description: t('upgrade.recommend-upgrading-description'),
    features: [
      t('upgrade.manager-view-feature-1'),
      t('upgrade.manager-view-feature-2'),
      t('upgrade.manager-view-feature-3'),
      t('upgrade.manager-view-feature-4'),
      t('upgrade.manager-view-feature-5'),
    ],
    buttonText: t('billing.upgrade-account'),
  },
  [UpgradeMessageSource.DELETE_ENTITY]: {
    title: t('organisation.delete-entity'),
    description: t('upgrade.delete-entity-description'),
    features: [
      {
        title: t('upgrade.complete-deletion-title'),
        description: t('upgrade.complete-deletion-description'),
      },
      {
        title: t('upgrade.view-only-title'),
        description: t('upgrade.view-only-description'),
      },
    ],
    hideFeatureIcon: true,
    buttonText: t('organisation.delete-entity'),
    buttonType: 'warning-red',
    image: DeleteEntityImage,
    secondButtonText: t('organisation.change-to-view-only'),
    secondButtonType: 'primary',
    footerText: t('upgrade.reactivate-view-only-footer'),
    footerTextBold: true,
  },
  [UpgradeMessageSource.DELETE_ENTITY_ONLY]: {
    title: t('organisation.delete-entity'),
    description: t('upgrade.delete-entity-only-description'),
    features: [],
    hideFeatureIcon: true,
    buttonText: t('organisation.delete-entity'),
    buttonType: 'warning-red',
    image: DeleteEntityImage,
  },
  [UpgradeMessageSource.CHANGE_TO_VIEW_ONLY]: {
    title: t('organisation.change-to-view-only'),
    description: t('upgrade.change-to-view-only-description'),
    features: [],
    buttonText: t('common.continue'),
    buttonType: 'primary',
    image: DeleteEntityImage,
  },
  [UpgradeMessageSource.REACTIVATE_ENTITY]: {
    title: t('organisation.reactivate-entity'),
    description: t('upgrade.reactivate-entity-description'),
    features: [],
    buttonText: t('upgrade.reactivate-button'),
    buttonType: 'primary',
    image: DeleteEntityImage,
    footerText: t('upgrade.reactivate-view-only-footer'),
    footerTextBold: true,
  },
  [UpgradeMessageSource.COMPARE_RETURN]: {
    title: t('upgrade.compare-return-title'),
    description: t('upgrade.compare-return-recommend-description'),
    features: [
      t('upgrade.compare-return-feature-1'),
      t('upgrade.compare-return-feature-2'),
      t('upgrade.compare-return-feature-3'),
      t('upgrade.compare-return-feature-4'),
    ],
    buttonText: t('billing.upgrade-account'),
  },
  [UpgradeMessageSource.OSS_RETURN]: {
    title: t('upgrade.oss-return-title'),
    description: t('upgrade.upgrading-plan-access-description'),
    features: [
      t('upgrade.oss-return-feature-1'),
      t('upgrade.oss-return-feature-2'),
      t('upgrade.oss-return-feature-3'),
      t('upgrade.oss-return-feature-4'),
      t('upgrade.oss-return-feature-5'),
    ],
    buttonText: t('billing.upgrade-account'),
  },
});

interface UpgradeMessageProps {
  source: UpgradeMessageSource;
  onClose: () => void;
  userAccountType?: string;
  addPadding?: boolean;
  callback?: () => void;
  secondButtonCallback?: () => void;
  goToBilling?: () => void;
  isFromCheckout?: boolean;
}

export const UpgradeMessage: React.FC<UpgradeMessageProps> = ({
  source,
  onClose,
  userAccountType,
  addPadding = false,
  callback,
  secondButtonCallback,
  goToBilling,
  isFromCheckout = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const content = React.useMemo(() => getContentConfig(t)[source], [t, source]);

  const {
    title,
    description,
    features,
    buttonText,
    buttonType = 'default',
    image,
    secondButtonText,
    secondButtonType = 'default',
    hideFeatureIcon = false,
    footerText,
    footerTextBold = false,
    showCancelButton = false,
    cancelButtonText = t('common.cancel'),
    showBillingNote = false,
    compactLayout = false,
  } = content;

  const handleAction = () => {
    if (callback) {
      callback();
      onClose();
      return;
    }

    if (userAccountType && userAccountType !== 'OWNER') {
      dispatch(
        turnMessageOn({
          title: t('billing.permission-denied-title'),
          description: t('billing.permission-denied-description'),
          type: MessageStates.WARNING,
        })
      );
      return;
    }
    navigate('/billing');
    onClose();
  };

  const handleSecondAction = () => {
    if (secondButtonCallback) {
      secondButtonCallback();
      onClose();
    }
  };

  const listTextStyle: React.CSSProperties = {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '20px',
    textAlign: 'left',
    color: 'var(--color-text-dark, #042a2a)',
    width: '100%',
  };
  const listItemStyle: React.CSSProperties = {
    display: 'flex',
    width: '100%',
    gap: 10,
  };

  const listCheckStyle: React.CSSProperties = {
    width: 15,
    height: 15,
    marginTop: 2,
  };

  const showPrimaryButton =
    !isFromCheckout || source !== UpgradeMessageSource.VAT_CHECKER_ACTIVATE;

  return (
    <div
      style={{
        boxShadow:
          source === UpgradeMessageSource.VAT_CHECKER
            ? '10px 10px 30px 0px #0000001A'
            : 'none',
        width: '100%',
        maxWidth: compactLayout ? 650 : 1200,
        borderRadius: 20,
        background: 'var(--color-surface-raised, #fff)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: addPadding ? '24px 24px' : '0 24px',
        gap: '24px',
        margin: '0 auto',
      }}
    >
      <img src={image || UpgradeAccountImage} alt={title} />
      <span style={{ fontWeight: 600, fontSize: 16, textAlign: 'center' }}>
        {title}
      </span>
      {description ? (
        <span style={{ fontSize: 14, fontWeight: 400, textAlign: 'center' }}>
          {description}
        </span>
      ) : null}
      {features.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            width: compactLayout ? 500 : undefined,
          }}
        >
          {features.map((feature, index) => (
            <div style={listItemStyle} key={index}>
              {hideFeatureIcon ? (
                <span style={{ fontSize: 20, lineHeight: '20px' }}>•</span>
              ) : (
                <CheckIcon style={listCheckStyle} />
              )}
              <span style={listTextStyle}>
                {typeof feature === 'string' ? (
                  feature
                ) : (
                  <>
                    {feature.title}
                    <span style={{ fontWeight: 400 }}>
                      {' - '}
                      {feature.description}
                    </span>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      {showBillingNote && (
        <span
          style={{
            fontSize: 14,
            fontWeight: 400,
            width: compactLayout ? 550 : undefined,
            textAlign: 'center',
          }}
        >
          {t('upgrade.vat-number-billing-note')}
          {!isFromCheckout && (
            <>
              <div className="divider-20" />
              {t('upgrade.addon-activation-billing-note')}
              <br />
              {t('upgrade.check-billing-for-details-prefix')}{' '}
              <span
                style={{
                  color: '#5e87b0',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={goToBilling}
              >
                {t('other.billing-page')}
              </span>{' '}
              {t('upgrade.check-billing-for-details-suffix')}
            </>
          )}
        </span>
      )}
      {footerText && (
        <span style={{ fontSize: 14, fontWeight: footerTextBold ? 600 : 400 }}>
          {footerText}
        </span>
      )}
      <div className="divider20" />
      <div style={{ display: 'flex', gap: 15, justifyContent: 'center' }}>
        {showCancelButton && (
          <ScopeButton
            type="default"
            width="140px"
            height="40px"
            onClick={onClose}
          >
            {isFromCheckout ? t('common.got-it') : cancelButtonText}
          </ScopeButton>
        )}
        {showPrimaryButton && (
          <ScopeButton
            type={buttonType}
            width="fit-content"
            height="40px"
            onClick={handleAction}
          >
            {buttonText}
          </ScopeButton>
        )}
        {secondButtonText && secondButtonCallback && (
          <ScopeButton
            type={secondButtonType}
            width="fit-content"
            height="40px"
            onClick={handleSecondAction}
          >
            {secondButtonText}
          </ScopeButton>
        )}
      </div>
    </div>
  );
};
